/**
 * Subscription API Functions
 *
 * API calls for subscription-related endpoints.
 */

import { api } from './client';
import { SubscriptionTier } from './auth';

// =============================================================================
// Types
// =============================================================================

export interface EventUsage {
  used: number;
  limit: number | null;
  remaining: number | null;
  isUnlimited: boolean;
}

export interface SeasonPassInfo {
  tier: SubscriptionTier;
  expiresAt: string;
}

export interface SubscriptionDetails {
  tier: SubscriptionTier;
  /** Better of subscription tier and an unexpired season pass. */
  effectiveTier: SubscriptionTier;
  tierName: string;
  usage: EventUsage;
  /** The quota month the usage bar counts against (calendar month, org TZ). */
  billingPeriod: {
    start: string;
    end: string;
  };
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  /** True when the subscription is cancelled but runs to the period end. */
  cancelAtPeriodEnd: boolean;
  seasonPass: SeasonPassInfo | null;
  hasStripeCustomer: boolean;
  paymentsEnabled: boolean;
}

/** SubscriptionTier values plus the one-time 'SEASON_PASS' pseudo-plan. */
export type PlanId = SubscriptionTier | 'SEASON_PASS';

export interface TierInfo {
  id: PlanId;
  name: string;
  monthlyEventLimit: number | null;
  priceMonthly: number;
  priceDisplay: string;
  oneTime: boolean;
  features: string[];
}

export interface GetSubscriptionResponse {
  subscription: SubscriptionDetails;
}

export interface GetTiersResponse {
  tiers: TierInfo[];
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current subscription and usage for the organization
 */
export async function getSubscription(): Promise<GetSubscriptionResponse> {
  const response = await api.get<GetSubscriptionResponse>('/subscription');
  return response.data;
}

/**
 * Get all available subscription tiers
 * This is a public endpoint (no auth required)
 */
export async function getTiers(): Promise<GetTiersResponse> {
  const response = await api.get<GetTiersResponse>('/subscription/tiers');
  return response.data;
}

// =============================================================================
// Billing (Stripe) — owner only
// =============================================================================

export interface CheckoutResponse {
  /** Hosted Stripe Checkout URL to redirect to… */
  url?: string;
  /** …or true when an existing subscription was switched in place. */
  updated?: boolean;
}

/** One-time text packs (Terms §5.9). */
export type SmsPackId = 'SMS_PACK_500' | 'SMS_PACK_2000';
export type CheckoutPlanId = Exclude<PlanId, 'FREE'> | SmsPackId;

/**
 * Start a purchase (new subscription, in-place plan switch, season pass, or text pack).
 */
export async function startCheckout(plan: CheckoutPlanId): Promise<CheckoutResponse> {
  const response = await api.post<CheckoutResponse>('/billing/checkout', { plan });
  return response.data;
}

/**
 * Open the Stripe Billing Portal (cancel subscription, update card).
 */
export async function openBillingPortal(): Promise<{ url: string }> {
  const response = await api.post<{ url: string }>('/billing/portal');
  return response.data;
}

// =============================================================================
// Texting allowance (owners and admins)
// =============================================================================

export interface SmsUsage {
  /** YYYY-MM in the org's timezone. */
  month: string;
  /** When the monthly allowance resets (local midnight on the 1st). */
  resetsAt: string;
  /** Complimentary org: never charged, unlimited texts. */
  complimentary: boolean;
  includedAllowance: number;
  includedUsed: number;
  packBalance: number;
  packUsed: number;
  /** Texts not sent this month because the allowance and packs ran out. */
  blocked: number;
  exempt: number;
  byKind: Record<string, number>;
  packs: Array<{ id: SmsPackId; texts: number; priceCents: number }>;
  /** Owners can buy packs online (not complimentary, payments configured). */
  purchasable: boolean;
}

/** Texts still available this month: included remainder plus pack balance. */
export function smsTextsRemaining(u: SmsUsage): number | null {
  if (u.complimentary) return null; // unlimited
  return Math.max(0, u.includedAllowance - u.includedUsed) + u.packBalance;
}

export async function getSmsUsage(): Promise<SmsUsage> {
  const response = await api.get<SmsUsage>('/billing/sms-usage');
  return response.data;
}

